import { CheckIcon } from 'lucide-react';
import { handleRovingKeyDown, rovingIndexFor } from '@/lib/roving-focus.js';

interface OptionListOption<T extends string | number> {
  value: T;
  label: string;
  /** Right-aligned beside the label — what picking this option costs. */
  detail?: string;
}

interface OptionListProps<T extends string | number> {
  options: OptionListOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
}

/** `SegmentedControl` stacked into rows, for choices with too many options to share one line. */
export function OptionList<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
}: OptionListProps<T>) {
  const values = options.map((o) => o.value);
  const rovingIndex = rovingIndexFor(values, value);

  return (
    <div className="sm-stack sm-option-list" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt, index) => (
        <button
          key={String(opt.value)}
          type="button"
          role="radio"
          className="sm-control sm-focus-ring sm-option"
          aria-checked={opt.value === value}
          tabIndex={index === rovingIndex ? 0 : -1}
          onKeyDown={(e) => handleRovingKeyDown(e, values, index, onChange)}
          onClick={() => onChange(opt.value)}
        >
          <span className="sm-center sm-paytype-check" aria-hidden="true">
            <CheckIcon size={12} strokeWidth={3} />
          </span>
          <span className="sm-type-body-strong sm-option-label">{opt.label}</span>
          {opt.detail !== undefined && (
            <span className="sm-type-body sm-tabular sm-option-detail">{opt.detail}</span>
          )}
        </button>
      ))}
    </div>
  );
}
